import { getBffBaseUrl, getVisitKey, TENANTS_PATH } from '@/lib/bff/config'

/** A VisitOne tenant the caller is authorised for. */
export type Tenant = {
  id: string
  name: string
}

/** Raised when the BFF rejects the access token (HTTP 401). */
export class TenantAuthError extends Error {
  constructor(message = 'Not authorised for tenant access') {
    super(message)
    this.name = 'TenantAuthError'
  }
}

/** Raised for any other failure (network, timeout, 5xx, malformed body). */
export class TenantRequestError extends Error {
  constructor(message = 'Could not resolve tenant access') {
    super(message)
    this.name = 'TenantRequestError'
  }
}

function normalise(data: unknown): Tenant[] {
  if (!Array.isArray(data)) throw new TenantRequestError('Unexpected tenant response shape')
  return data.map((entry) => {
    if (
      !entry ||
      typeof entry !== 'object' ||
      typeof (entry as Tenant).id !== 'string' ||
      typeof (entry as Tenant).name !== 'string'
    ) {
      throw new TenantRequestError('Unexpected tenant response shape')
    }
    const { id, name } = entry as Tenant
    return { id, name }
  })
}

/*
 * Resolve the tenants the caller is authorised for. The Auth0 access token is
 * sent as a Bearer credential and the environment-scoped x-visit-key as a
 * header; the BFF derives the tenant list from the token alone (no
 * client-supplied tenant filter). The URL is built from the configured BFF base
 * URL, falling back to the current host when none is configured.
 */
export async function fetchTenants(token: string, signal?: AbortSignal): Promise<Tenant[]> {
  const url = `${getBffBaseUrl('mmv')}${TENANTS_PATH}`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-visit-key': getVisitKey(),
        Accept: 'application/json',
      },
      signal,
    })
  } catch {
    throw new TenantRequestError('Network error while resolving tenants')
  }

  if (response.status === 401) throw new TenantAuthError()
  if (!response.ok) throw new TenantRequestError(`Tenant resolution failed (${response.status})`)

  let data: unknown
  try {
    data = await response.json()
  } catch {
    throw new TenantRequestError('Malformed tenant response')
  }

  return normalise(data)
}
